import React, { useState, useMemo } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

/* ===========================
   MASTER SOP STRUCTURE
=========================== */

const SOP_LAYERS = {
  company: [
    { id: "safety", title: "Safety Basics", requiredForShift: true },
    { id: "discipline", title: "Discipline & Ethics" },
    { id: "qualityMindset", title: "Quality Mindset" },
    { id: "housekeeping", title: "Housekeeping (5S)", requiredForClose: true },
  ],
  machine: [
    { id: "startup", title: "Start-Up SOP", requiredForShift: true },
    { id: "operation", title: "Operation SOP" },
    { id: "quality", title: "Quality SOP" },
    { id: "shutdown", title: "Shutdown SOP", requiredForClose: true },
    { id: "breakdown", title: "Breakdown SOP" },
  ],
  offMachine: [
    { id: "helper", title: "Helper / Material SOP" },
    { id: "qc", title: "QC SOP" },
    { id: "store", title: "Store SOP" },
    { id: "supervisor", title: "Supervisor SOP" },
  ],
  maintenance: [
    { id: "preventive", title: "Preventive Maintenance" },
    { id: "tooling", title: "Tooling SOP" },
    { id: "utilities", title: "Utilities SOP" }, // impacts adherence
  ],
};

/* ===========================
   CHECKLIST ENGINE
=========================== */

const CHECKLISTS = {
  safety: ["Wear PPE", "Inspect guards"],
  startup: ["Check oil", "Clear surface"],
  housekeeping: ["Clean machine", "Remove scrap"],
  shutdown: ["Switch off power"],
  utilities: ["Check compressor", "Check voltage"],
};

/* ===========================
   MAIN COMPONENT
=========================== */

export default function SOPHubHome() {
  const [role, setRole] = useState("Worker");
  const [shiftStage, setShiftStage] = useState("Not Started");
  const [completedSOPs, setCompletedSOPs] = useState([]);
  const [activeSOP, setActiveSOP] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [deviations, setDeviations] = useState([]);
  const [skills, setSkills] = useState({});
  const [uiAlert, setUiAlert] = useState(null);

  const [voiceMode, setVoiceMode] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [listening, setListening] = useState(false);
  const [sopStartTime, setSopStartTime] = useState(null);
  const [sopEndTime, setSopEndTime] = useState(null);
  const [itemTimings, setItemTimings] = useState({});

  /* ===========================
     SHIFT ENFORCEMENT
  ============================ */

  const requiredStart = [
    "safety",
    "startup",
  ];

  const requiredClose = [
    "housekeeping",
    "shutdown",
  ];

  const canStartMachine = requiredStart.every((s) => completedSOPs.includes(s));

  const canCloseShift = requiredClose.every((s) => completedSOPs.includes(s));

  const startShift = () => {
    if (!canStartMachine) {
      setUiAlert({
        type: "warning",
        message: "Complete Safety + Start-Up SOP first.",
      });
      return;
    }

    setUiAlert(null);
    setShiftStage("Machine Running");
    speak("Machine started. Shift is now running.");    
  };

  const closeShift = () => {
    if (!canCloseShift) {
      setUiAlert({
        type: "warning",
        message: "Complete Shutdown + Housekeeping first.",
      });
      return;
    }

    setUiAlert(null);
    setShiftStage("Closed");
    speak("Shift closed successfully. Good work.");
  };

// ===========================
// TTS ENGINE
// ===========================

const speak = (text) => {
    if (!("speechSynthesis" in window)) return;

    window.speechSynthesis.cancel(); 

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.95; 
    utterance.pitch = 1;
    utterance.volume = 1;

    // Optional: select better voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find((v) =>
      v.name.toLowerCase().includes("india")
    );
    if (preferred) utterance.voice = preferred;

    window.speechSynthesis.speak(utterance);
};

const stopSpeaking = () => {
    window.speechSynthesis.cancel();
}; 

const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  let recognition;

  if (SpeechRecognition) {
    recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-IN";
  }

  const startListening = () => {
    if (!recognition) return;

    setListening(true);

    recognition.start();

    recognition.onresult = (event) => {
      const transcript =
        event.results[0][0].transcript.toLowerCase();

      if (transcript.includes("done")) {
        handleVoiceDone();
      }
    };

    recognition.onend = () => {
      setListening(false);
    };
  };

  const handleVoiceDone = () => {
    const items = CHECKLISTS[activeSOP] || [];
    const currentItem = items[currentIndex];

    // Mark checkbox true
    toggleCheck(currentItem);

    // Capture item end time
    const end = Date.now();

    setItemTimings((prev) => ({
      ...prev,
      [currentItem]: {
        ...prev[currentItem],
        endTime: end,
        duration:
          end - (prev[currentItem]?.startTime || end),
      },
    }));

    if (currentIndex + 1 < items.length) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);

      speak(`Next step. ${items[nextIndex]}`);

      // Start timer for next item
      setItemTimings((prev) => ({
        ...prev,
        [items[nextIndex]]: {
          startTime: Date.now(),
        },
      }));

      setTimeout(() => {
        startListening();
      }, 800);
    } else {
      // Completed all
      setSopEndTime(Date.now());
      speak("All checklist items completed.");
    }
  };

  const startVoiceChecklist = () => {
    const items = CHECKLISTS[activeSOP] || [];

    if (!items.length) return;

    setVoiceMode(true);
    setCurrentIndex(0);
    setSopStartTime(Date.now());

    speak(`Voice checklist started. First step. ${items[0]}`);

    // Start first item timer
    setItemTimings({
      [items[0]]: {
        startTime: Date.now(),
      },
    });

    setTimeout(() => {
      startListening();
    }, 1200);
  };

  const toggleCheck = (item) => {
    setCheckedItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  const completeSOP = () => {
    const items = CHECKLISTS[activeSOP] || [];
    const allChecked = items.every((i) => checkedItems[i]);

    if (!allChecked) {
      setUiAlert({
        type: "error",
        message: "Complete all checklist items before confirming.",
      });
      return;
    }    

    setCompletedSOPs((prev) => [...new Set([...prev, activeSOP])]);
    setSkills((prev) => ({
      ...prev,
      [activeSOP]: "Certified",
    }));
    setActiveSOP(null);
    setCheckedItems({});
  };

  /* ===========================
     DEVIATION SYSTEM
  ============================ */

  const reportDeviation = () => {
    const newDev = {
      id: Date.now(),
      type: "Quality",
      date: new Date().toLocaleDateString(),
    };
    setDeviations((prev) => [...prev, newDev]);
  };

  /* ===========================
     ANALYTICS ENGINE
  ============================ */

  const totalSOPCount = Object.values(SOP_LAYERS).flat().length;

  const adherencePercent = Math.round(
    (completedSOPs.length / totalSOPCount) * 100
  );

  const utilitiesImpact =
    completedSOPs.includes("utilities") ? 0 : 5;

  const finalAdherence = Math.max(
    adherencePercent - utilitiesImpact,
    0
  );

  const donutData = {
    labels: ["Adhered", "Pending"],
    datasets: [
      {
        data: [finalAdherence, 100 - finalAdherence],
        backgroundColor: ["#84cc16", "#991b1b"],
      },
    ],
  };

  const barData = {
      labels: ["Total Deviations"],
      datasets: [
        {
          label: "Deviation Count",
          data: [deviations.length],
          backgroundColor: "#84cc16",
        },
      ],
    };

  //   const barOptions = {
  //     responsive: true,
  //     maintainAspectRatio: false,
  //     plugins: {
  //       legend: {
  //         labels: {
  //           color: "#ffffff",
  //         },
  //       },
  //       tooltip: {
  //         titleColor: "#ffffff",
  //         bodyColor: "#84cc16",
  //       },
  //     },
  //     scales: {
  //       x: {
  //         ticks: { color: "#ffffff" },
  //         grid: { color: "#374151" },
  //       },
  //       y: {
  //         ticks: { color: "#ffffff" },
  //         grid: { color: "#374151" },
  //       },
  //     },
  // };

  const lineData = {
    labels: deviations.map((d) => d.date),
    datasets: [
      {
        label: "Deviation Trend",
        data: deviations.map((_, i) => i + 1),
        borderColor: "#84cc16",
      },
    ],
  };

  const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: "#ffffff",
          },
        },
        tooltip: {
          titleColor: "#ffffff",
          bodyColor: "#84cc16",
        },
      },
      scales: {
        x: {
          ticks: { color: "#ffffff" },
          grid: { color: "#374151" },
        },
        y: {
          ticks: { color: "#ffffff" },
          grid: { color: "#374151" },
        },
      },
  };

  /* ===========================
     RENDER
  ============================ */

  const renderSOPLayer = (title, layerKey) => (
    <>
      <div className="col-span-full text-xl font-bold text-lime-400 mt-6">
        {title}
      </div>
      {SOP_LAYERS[layerKey].map((sop) => {
        const completed = completedSOPs.includes(sop.id);
        return (
          <div
            key={sop.id}
            onClick={() => {
              setActiveSOP(sop.id);
              speak(`Opening ${sop.title} standard operating procedure.`);
            }}
            className={`p-4 rounded border cursor-pointer ${
              completed
                ? "border-lime-500 bg-lime-900"
                : "border-red-700 bg-gray-900"
            }`}
          >
            <h4 className="text-white">{sop.title}</h4>
            <p
              className={`text-sm mt-2 font-semibold ${
                completed ? "text-green-400" : "text-orange-400"
              }`}
            >
              {completed ? "Completed" : "Pending"}
            </p>
          </div>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-black [&_p]:text-white text-green-400 p-6 font-mono">
      <h1 className="text-xl lg:text-2xl text-yellow-300 font-bold text-center">Think5Whys – SOP Operating System</h1>
      {uiAlert && (
        <div
          className={`mt-2 px-4 py-1 rounded border flex justify-between items-center ${
            uiAlert.type === "error"
              ? "bg-gray-800 border-red-600 text-yellow-200"
              : "bg-gray-700 border-orange-600 text-yellow-200"
          }`}
        >
          <span>{uiAlert.message}</span>

          <button
            onClick={() => setUiAlert(null)}
            className="w-12 bg-red-600 text-white ml-4 text-sm font-bold"
          >
            ✕
          </button>
        </div>
      )}      
      {/* Role Switch */}
      <div className="text-center mt-4">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-12 bg-gray-900 p-2 border border-lime-600 rounded"
        >
          <option>Worker</option>
          <option>Supervisor</option>
          <option>Owner</option>
        </select>
      </div>

      {/* SHIFT CONTROL */}
      <div className="text-center mt-6">
        <p className="text-white">Shift Stage: {shiftStage}</p>
        {shiftStage === "Not Started" && (
          <button
            onClick={startShift}
            className="w-40 bg-lime-500 text-black px-4 py-2 rounded"
          >
            Start Machine
          </button>
        )}
        {shiftStage === "Machine Running" && (
          <button
            onClick={closeShift}
            className="w-40 bg-red-600 px-4 py-2 rounded"
          >
            Close Shift
          </button>
        )}
      </div>

      {/* SOP GRID */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
        {renderSOPLayer("A. Company SOPs", "company")}
        {renderSOPLayer("B. On-Machine SOPs", "machine")}
        {renderSOPLayer("C. Off-Machine SOPs", "offMachine")}
        {renderSOPLayer("D. Maintenance & Utilities", "maintenance")}
      </div>

      {/* CHECKLIST VIEW */}
      {/* {activeSOP && (
        <div className="mt-10 bg-gray-900 p-6 rounded border border-lime-600">
          <h3 className="text-xl mb-4 text-yellow-200">Checklist</h3>
          {(CHECKLISTS[activeSOP] || []).map((item) => (
            <div key={item}>
              <input
                type="checkbox"
                checked={checkedItems[item] || false}
                onChange={() => toggleCheck(item)}
              />
              <span className="ml-2">{item}</span>
            </div>
          ))}
          <button
            onClick={completeSOP}
            className="mt-4 bg-lime-500 text-black px-4 py-2 rounded"
          >
            Confirm Completion
          </button>
          <div className="space-x-2 mt-2">
              <button
                onClick={() => {
                  const items = CHECKLISTS[activeSOP] || [];
                  speak(items.join(". "));
                }}
                className="w-40 bg-blue-600 px-3 py-1 rounded text-sm"
              >
                🔊 Read
              </button>
              <button
                onClick={stopSpeaking}
                className="w-40 bg-gray-700 px-3 py-1 rounded text-sm"
              >
                ⏹ Stop
              </button>
            </div>          
        </div>
      )} */}
      {/* CHECKLIST VIEW */}
      {activeSOP && (
        <div className="mt-10 bg-gray-900 p-6 rounded border border-lime-600">

          <div className="flex flex-col items-center mb-2">
            <h3 className="text-xl text-yellow-200">
              Checklist {voiceMode && "(Voice Mode Active)"}
            </h3>

            <div className="space-x-2">
              <button
                onClick={startVoiceChecklist}
                className="w-36 bg-blue-600 px-1 py-1 rounded text-xs"
              >
                🎙 Start Voice Mode
              </button>

              <button
                onClick={() => {
                  const items = CHECKLISTS[activeSOP] || [];
                  speak(items.join(". "));
                }}
                className="w-32 bg-green-600 px-1 py-1 rounded text-xs"
              >
                🔊 Read All
              </button>

              <button
                onClick={stopSpeaking}
                className="w-24 bg-gray-700 px-1 py-1 rounded text-xs"
              >
                ⏹ Stop
              </button>
            </div>
          </div>

          {(CHECKLISTS[activeSOP] || []).map((item, index) => (
            <div
              key={item}
              className={`p-2 rounded ${
                voiceMode && index === currentIndex
                  ? "bg-yellow-900"
                  : ""
              }`}
            >
              <input
                type="checkbox"
                checked={checkedItems[item] || false}
                onChange={() => toggleCheck(item)}
              />
              <span className="ml-2">{item}</span>

              {itemTimings[item]?.duration && (
                <span className="ml-4 text-xs text-lime-400">
                  {Math.round(itemTimings[item].duration / 1000)} sec
                </span>
              )}
            </div>
          ))}

          <button
            onClick={completeSOP}
            className="mt-4 bg-lime-500 text-black px-4 py-2 rounded"
          >
            Confirm Completion
          </button>

          {sopStartTime && sopEndTime && (
            <div className="mt-4 text-sm text-yellow-300">
              Total Time:{" "}
              {Math.round((sopEndTime - sopStartTime) / 1000)} seconds
            </div>
          )}

          {listening && (
            <div className="mt-2 text-blue-400 animate-pulse">
              🎤 Listening... Say "Done"
            </div>
          )}
        </div>
      )}      

      {/* DEVIATION */}
      {role === "Worker" && (
        <div className="text-center mt-6">
          <button
            onClick={reportDeviation}
            className="bg-red-700 px-4 py-2 rounded"
          >
            Report Deviation
          </button>
        </div>
      )}

      {/* ANALYTICS DASHBOARD */}
      {(role === "Owner" || role === "Supervisor") && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
          <div className="bg-gray-900 p-4 rounded h-64">
            <h3 className="text-center mb-4 text-yellow-200">SOP Adherence</h3>
            <Doughnut data={donutData} options={chartOptions}/>
          </div>

          <div className="h-64">
            <h3 className="text-center mb-4 text-yellow-200">Deviation Count</h3>
            <Bar data={barData} options={chartOptions}/>
          </div>

          <div className="bg-gray-900 p-4 rounded h-64">
            <h3 className="text-center mb-4 text-yellow-200">Deviation Trend</h3>
            <Line data={lineData}  options={chartOptions}/>
          </div>
        </div>
      )}

      <footer className="mt-8 text-center italic text-yellow-200">
        Not who is working hard — but who is working right.
      </footer>
    </div>
  );
}